import { shouldMakeAPICall, isOnline } from '../utils/network';
import { prRepository } from '../repositories/pr.repository';
import { masterDataRepository } from '../repositories/master-data.repository';
import { offlineQueue } from '../queue/offlineQueue';

/**
 * Sync Engine
 * Handles automatic synchronization of all data
 */
export class SyncEngine {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
  }

  /**
   * Sync all initial load data
   */
  async syncAllInitialData(authToken, userToken, orgId) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      console.warn('⚠️ Sync skipped - offline or not authenticated');
      return;
    }

    if (this.isSyncing) {
      console.warn('⚠️ Sync already in progress');
      return;
    }

    this.isSyncing = true;

    try {
      console.log('🔄 Starting full data sync...');

      // Sync all data in parallel
      const syncResults = await Promise.allSettled([
        prRepository.syncCombinedPRsFromAPI(authToken, userToken, orgId).catch(() => 
          prRepository.syncFromAPI(authToken, userToken, orgId)
        ),
        masterDataRepository.syncCostCenters(authToken, userToken),
        masterDataRepository.syncGLCodes(authToken, userToken),
        masterDataRepository.syncProjectCodes(authToken, userToken),
        masterDataRepository.syncItems(authToken, userToken),
        masterDataRepository.syncDepartments(authToken, userToken),
        masterDataRepository.syncVendors(authToken, userToken),
        masterDataRepository.syncUsers(authToken, userToken),
        masterDataRepository.syncPRCodes(authToken),
        // RFQ Master Data
        masterDataRepository.syncCountries(authToken),
        masterDataRepository.syncStates(authToken, null), // Load all states
        masterDataRepository.syncCities(authToken, null), // Load all cities
        masterDataRepository.syncRFQCategories(authToken, userToken),
        masterDataRepository.syncShippingAddresses(authToken, userToken),
      ]);
      
      console.log('✅ Full data sync completed');
    } catch (error) {
      console.error('❌ Error during sync:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync offline queue
   */
  async syncQueue(authToken, userToken, orgId) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return;
    }

    try {
      const pendingActions = await offlineQueue.getPending();
      
      if (pendingActions.length === 0) {
        return;
      }

      console.log(`🔄 Syncing ${pendingActions.length} queued actions...`);

      for (const action of pendingActions) {
        console.log(`🔄 Processing queued action: ${action.action} for ${action.endpoint}`);
        console.log('Action payload keys:', Object.keys(action.payload || {}));
        
        const success = await offlineQueue.processAction(action, authToken, userToken);
        
        if (success) {
          console.log(`✅ Action ${action.action} completed successfully`);
          
          // If PR update/create was successful, refresh PR list
          if (action.endpoint.includes('ins_purchase_request')) {
            console.log('✅ PR action completed, refreshing PR list...');
            // Refresh PR list to get updated data from server
            await prRepository.syncCombinedPRsFromAPI(authToken, userToken, orgId).catch(() => 
              prRepository.syncFromAPI(authToken, userToken, orgId)
            );
          }
          
        } else {
          console.warn(`⚠️ Action ${action.action} failed, will retry later`);
        }
        
        // Small delay between actions
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      console.log('✅ Queue sync completed');
    } catch (error) {
      console.error('❌ Error syncing queue:', error);
    }
  }

  /**
   * Start automatic sync
   */
  startAutoSync(authToken, userToken, orgId, intervalMs = 30000) {
    if (this.syncInterval) {
      this.stopAutoSync();
    }

    this.syncInterval = setInterval(async () => {
      if (isOnline() && shouldMakeAPICall(authToken, userToken)) {
        await this.syncQueue(authToken, userToken, orgId);
      }
    }, intervalMs);

    console.log('✅ Auto-sync started');
  }

  /**
   * Stop automatic sync
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('⏹️ Auto-sync stopped');
    }
  }

  /**
   * Manual sync trigger
   */
  async manualSync(authToken, userToken, orgId) {
    await this.syncAllInitialData(authToken, userToken, orgId);
    await this.syncQueue(authToken, userToken, orgId);
  }

  /**
   * Sync on network reconnect
   */
  async syncOnReconnect(authToken, userToken, orgId) {
    if (isOnline()) {
      console.log('🌐 Network reconnected, syncing...');
      
      // Always try to sync queue first (user actions are priority)
      console.log('🔄 Priority: Syncing queued actions first...');
      await this.syncQueue(authToken, userToken, orgId);
      
    }
  }
}

export const syncEngine = new SyncEngine();
